import { useState } from 'react'
import { FormattedMessage } from 'react-intl'
import { useMutation, useApolloClient, gql } from '@apollo/client'
import styled from 'styled-components'
import { isEmpty } from 'lodash'

import { Button } from 'components/v2/Button'
import Heading from 'components/v2/Heading'
import { ErrorMessage } from 'components/v2/ErrorMessage'
import Modal from 'components/v2/Modal'
import { TotalCancellation } from './Total'
import { PartialCancellation } from './Partial'
import { resolvers as salesResolvers } from 'resolvers/sales'
import { getUnprocessableErrors } from 'utils/error'

const ModalContainer = styled(Modal.Container)`
  max-width: 700px;
`

const ModalHeader = styled(Modal.Header)`
  border-bottom: none;
`

const Tabs = styled.div<TSFixMe>`
  border-bottom: 1px solid rgba(153, 153, 189, 0.3);
  display: flex;
  padding: 0 20px;
  margin-bottom: 20px;
`

const Tab = styled.div<TSFixMe>`
  cursor: pointer;
  font-size: 15px;
  border-bottom: ${(props): TSFixMe => (props.isActive ? '3px solid #80deea' : 'none')};
  padding: ${(props): TSFixMe => (props.isActive ? '0 10px 7px' : '0 10px 10px')};
  font-weight: ${(props): TSFixMe => (props.isActive ? '500' : 'normal')};
`

const CANCEL_SALE_ITEMS = gql`
  mutation CancelSaleItems($saleId: ID!, $lineItems: [CancelSaleItemsLineItemInput!]!) {
    cancelSaleItems(input: { id: $saleId, lineItems: $lineItems })
  }
`

const LOCAL_CANCEL_SALE_ITEMS = gql`
  mutation LocalCancelSaleItems($saleId: ID!, $cancelledItems: [CancelSaleItemsLineItemInput!]!) {
    cancelSaleItems(id: $saleId, cancelledItems: $cancelledItems) @client
  }
`

export const CancelSaleModal = ({ sale, closeModal }: TSFixMe): TSFixMe => {
  const client = useApolloClient()
  client.addResolvers(salesResolvers)

  const [tabIndex, setTabIndex] = useState(1)
  const [lineItems, setLineItems] = useState(
    // @ts-expect-error TS(2559) FIXME Remove this comment to see the full error message
    sale.lineItems.map((lineItem): TSFixMe => {
      return {
        ...lineItem,
        id: lineItem.itemID,
        cancelledQuantity: 0
      }
    })
  )

  const [localCancelSaleItems] = useMutation(LOCAL_CANCEL_SALE_ITEMS)
  const [cancelSaleItems, { loading, error }] = useMutation(CANCEL_SALE_ITEMS, {
    onCompleted() {
      localCancelSaleItems({
        variables: {
          saleId: sale.id,
          cancelledItems: lineItems.map((lineItem: TSFixMe): TSFixMe => {
            return {
              id: lineItem.id,
              quantity: lineItem.cancelledQuantity
            }
          })
        }
      })

      closeModal()
    }
  })

  const handleCancelSaleItems = (): TSFixMe => {
    const isTotalCancellation = tabIndex === 1

    if (isTotalCancellation) {
      setLineItems(
        sale.lineItems.map((lineItem: TSFixMe): TSFixMe => {
          return {
            ...lineItem,
            id: lineItem.itemID,
            cancelledQuantity: lineItem.quantity
          }
        })
      )

      setTimeout((): TSFixMe => {
        cancelSaleItems({
          variables: {
            saleId: sale.id,
            lineItems: sale.lineItems.map((lineItem: TSFixMe): TSFixMe => {
              return {
                id: lineItem.itemID,
                quantity: lineItem.quantity
              }
            })
          }
        })
      })
    } else {
      const filteredCancelledItems = lineItems.filter(
        ({ cancelledQuantity }: TSFixMe): TSFixMe => cancelledQuantity > 0
      )

      if (filteredCancelledItems.length > 0) {
        cancelSaleItems({
          variables: {
            saleId: sale.id,
            lineItems: filteredCancelledItems.map((lineItem: TSFixMe): TSFixMe => {
              return {
                id: lineItem.id,
                quantity: lineItem.cancelledQuantity
              }
            })
          }
        })
      }
    }
  }

  const isPartialCancellation = tabIndex === 2
  const filteredCancelledItems = lineItems.filter(({ cancelledQuantity }: TSFixMe): TSFixMe => cancelledQuantity > 0)

  return (
    <ModalContainer large>
      <ModalHeader onClose={closeModal}>
        <Heading.H5>
          <FormattedMessage id="spanish.sales.cancellation.title" defaultMessage="Cancelar venta" />
        </Heading.H5>
      </ModalHeader>

      {isEmpty(sale.coupon) && (
        <Tabs>
          <Tab
            isActive={tabIndex === 1}
            onClick={(): TSFixMe => {
              setTabIndex(1)
            }}
            isFirst
          >
            <FormattedMessage id="spanish.dictionary.total" defaultMessage="Total" />
          </Tab>

          <Tab
            isActive={tabIndex === 2}
            onClick={(): TSFixMe => {
              setTabIndex(2)
            }}
            isLast
          >
            <FormattedMessage id="spanish.dictionary.partial" defaultMessage="Parcial" />
          </Tab>
        </Tabs>
      )}

      <Modal.Content>
        {tabIndex === 1 && <TotalCancellation sale={sale} />}

        {tabIndex === 2 && <PartialCancellation sale={sale} lineItems={lineItems} setLineItems={setLineItems} />}
      </Modal.Content>

      <Modal.Footer>
        {error && (
          <Modal.ErrorMessage>
            <ErrorMessage errors={getUnprocessableErrors(error)}>
              <FormattedMessage
                id="spanish.sales.cancellation.error"
                defaultMessage="Hubo un error al cancelar la venta"
              />
            </ErrorMessage>
          </Modal.ErrorMessage>
        )}
        <Modal.Actions>
          <Button kind="cancel" onClick={(): TSFixMe => closeModal()}>
            <FormattedMessage id="spanish.dictionary.close" defaultMessage="Cerrar" />
          </Button>
          <Button
            kind="primary"
            loading={loading}
            onClick={handleCancelSaleItems}
            disabled={isPartialCancellation && filteredCancelledItems.length === 0}
          >
            <FormattedMessage id="spanish.sales.cancellation.action" defaultMessage="Cancelar venta" />
          </Button>
        </Modal.Actions>
      </Modal.Footer>
    </ModalContainer>
  )
}
